import { Injectable, signal, computed, inject, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import { APIParserService } from './api-parser.service';
import { ApiEndpoint } from './endpoint.interface';
import { ApiNavigation } from './navigation.interface';
import { API_HOST } from './host.provider';
import { APISpec } from './api-spec.interface';

/**
 * Service for managing API documentation state and operations
 * Handles loading OpenAPI specifications and provides parsed data
 */
@Injectable({
  providedIn: 'root',
})
export class ApiDocsService {
  private readonly http = inject(HttpClient);
  private readonly parser = inject(APIParserService);
  private readonly host = inject(API_HOST);

  // Signals for state management

  /** Loading state signal */
  private readonly _isLoading = signal(false);

  /** Error state signal */
  private readonly _error = signal<string | null>(null);

  /** OpenAPI specification signal */
  private readonly _spec = signal<APISpec | null>(null);

  /** Parsed endpoints signal */
  private readonly _endpoints = signal<ApiEndpoint[]>([]);

  /** Navigation structure signal */
  private readonly _navigation = signal<ApiNavigation | null>(null);

  /** Selected endpoint ID signal */
  private readonly _selectedEndpointId = signal<string | null>(null);

  // Public readonly getters

  /** Whether the service is currently loading data */
  readonly isLoading = this._isLoading.asReadonly();

  /** Current error message if any */
  readonly error = this._error.asReadonly();

  /** Loaded OpenAPI specification */
  readonly spec = this._spec.asReadonly();

  /** All parsed API endpoints */
  readonly endpoints = this._endpoints.asReadonly();

  /** Navigation structure for displaying grouped endpoints */
  readonly navigation = this._navigation.asReadonly();

  /** Currently selected endpoint ID */
  readonly selectedEndpointId = this._selectedEndpointId.asReadonly();

  // Computed signals

  /**
   * Currently selected endpoint based on selectedEndpointId
   * @returns The selected endpoint or null if none selected
   */
  readonly selectedEndpoint = computed(() => {
    const id = this._selectedEndpointId();
    return id ? this._endpoints().find(e => e.id === id) : null;
  });

  /**
   * Whether the service is ready (loaded without errors)
   * @returns True if data is loaded and no errors occurred
   */
  readonly isReady = computed(() => {
    return !this._isLoading() && this._spec() !== null && !this._error();
  });

  /**
   * Initialize the service by loading and parsing the OpenAPI specification
   * @returns Promise that resolves when initialization is complete
   * @throws Error if loading or parsing fails
   */
  async initialize(): Promise<void> {
    // Prevent multiple simultaneous initializations
    if (this._isLoading()) {
      return;
    }

    this._isLoading.set(true);
    this._error.set(null);

    try {
      // Step 1: Load the OpenAPI specification
      const spec = await this.loadOpenApiSpec();
      this._spec.set(spec);

      // Step 2: Parse the specification using the async parser
      const { endpoints, navigation } = await this.parser.parseSpec(spec);

      // Step 3: Update state with parsed data
      this._endpoints.set(endpoints);
      this._navigation.set(navigation);

    } catch (error) {
      console.error('Failed to initialize API docs:', error);

      // Set user-friendly error message
      let errorMessage = 'Failed to load API documentation';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String((error as { message: unknown }).message);
      }

      this._error.set(errorMessage);

      // Reset other state on error
      this._spec.set(null);
      this._endpoints.set([]);
      this._navigation.set(null);
      this._selectedEndpointId.set(null);
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Select an endpoint by its ID
   * @param endpointId - The ID of the endpoint to select
   */
  selectEndpoint(endpointId: string): void {
    const endpoint = this._endpoints().find(e => e.id === endpointId);
    if (endpoint) {
      this._selectedEndpointId.set(endpointId);
    } else {
      console.warn(`Endpoint with id "${endpointId}" not found`);
      // Optionally clear selection if endpoint not found
      this._selectedEndpointId.set(null);
    }
  }

  /**
   * Clear the current endpoint selection
   */
  clearSelection(): void {
    this._selectedEndpointId.set(null);
  }

  /**
   * Refresh the API documentation by reloading and reparsing
   * @returns Promise that resolves when refresh is complete
   */
  async refresh(): Promise<void> {
    await this.initialize();
  }

  /**
   * Get endpoint by ID
   * @param endpointId - The endpoint ID
   * @returns The endpoint or undefined if not found
   */
  getEndpointById(endpointId: string): ApiEndpoint | undefined {
    return this._endpoints().find(e => e.id === endpointId);
  }

  /**
   * Get endpoints by tag
   * @param tag - The tag name
   * @returns Array of endpoints with the specified tag
   */
  getEndpointsByTag(tag: string): ApiEndpoint[] {
    return this._endpoints().filter(e => e.tags.includes(tag));
  }

  /**
   * Search endpoints by text
   * @param searchText - Text to search for
   * @returns Array of matching endpoints
   */
  searchEndpoints(searchText: string): ApiEndpoint[] {
    const lowerSearch = searchText.toLowerCase();
    return this._endpoints().filter(
      e =>
        e.path.toLowerCase().includes(lowerSearch) ||
        e.summary?.toLowerCase().includes(lowerSearch) ||
        e.description?.toLowerCase().includes(lowerSearch) ||
        e.operationId?.toLowerCase().includes(lowerSearch) ||
        e.tags.some(tag => tag.toLowerCase().includes(lowerSearch))
    );
  }

  /**
   * Load the OpenAPI specification from the configured endpoint
   * @returns Promise that resolves with the loaded specification
   * @private
   */
  private async loadOpenApiSpec(): Promise<APISpec> {
    try {
      return await firstValueFrom(
        this.http.get<APISpec>(this.host).pipe(
          catchError(error => {
            console.error('Failed to load from primary source:', error);

            // In development mode, try to load from local file
            if (isDevMode()) {
              return this.http.get<APISpec>('/jsons/openapi.json');
            }

            throw error;
          })
        )
      );
    } catch (error) {
      console.error('Failed to load OpenAPI specification:', error);

      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('404')) {
          throw new Error('API specification not found. Please check the API endpoint configuration.');
        } else if (error.message.includes('Network')) {
          throw new Error('Network error. Please check your internet connection.');
        }
      }

      throw error;
    }
  }

  /**
   * Get API metadata from the loaded specification
   * @returns API metadata or null if not loaded
   */
  getApiMetadata(): { title: string; version: string; description?: string } | null {
    const spec = this._spec();
    if (!spec || !spec.info) {
      return null;
    }

    return {
      title: spec.info.title || 'API',
      version: spec.info.version || '1.0.0',
      description: spec.info.description,
    };
  }

  /**
   * Check if the service has been initialized
   * @returns True if the service has been initialized (successfully or with error)
   */
  isInitialized(): boolean {
    return this._spec() !== null || this._error() !== null;
  }

  /**
   * Get the base URL for API calls
   * @returns The base URL from the OpenAPI spec or the configured host
   */
  getBaseUrl(): string {
    const spec = this._spec();

    // OpenAPI 3.0 uses servers instead of host
    if (spec?.servers && spec.servers.length > 0) {
      const serverUrl = spec.servers[0].url;
      // Handle relative URLs
      if (serverUrl.startsWith('/')) {
        const url = new URL(this.host);
        return `${url.protocol}//${url.host}${serverUrl}`;
      }
      // URL with a protocol
      if (serverUrl.startsWith('http://') || serverUrl.startsWith('https://')) {
        return serverUrl;
      }
      // URL without a protocol (e.g. "api-qa.imagise.cloud")
      return `https://${serverUrl}`;
    }

    // Fall back to the configured host
    return this.host.replace(/\/(swagger|openapi)\.json$/, '');
  }
}
